/**
 * C 端分类导航工具。
 *
 * 跳转优先级（与后端 CategoryNavBuilder 一致）：
 * 1. 一级配置了 nav_route → 必须走路由（聚合页/门户），与 jump_page_flag 无关
 * 2. 一级无 nav_route → 分类 Tab 展示右侧二级；jump_page_flag 仅二级点击或首页个别格子走 getEntrance
 * 3. category_scene 作补充（蛋糕/电影/礼包档位/三方 H5 过滤等），不替代 nav_route
 *
 * MALL_GOODS：后端 CategoryNavScopeEnum 查询参数，非数据库字段；
 * 用于商品列表顶部分类 Tab（排除聚合门户一级、三方 H5 二级）。
 */

const SCENE = {
  MALL_CAKE: 'MALL_CAKE',
  MALL_MOVIE: 'MALL_MOVIE',
  GIFT_PORTAL: 'GIFT_PORTAL',
  THIRD_H5: 'THIRD_H5',
};

/** 商品列表顶部分类树查询 scope（后端枚举，非表字段） */
export const GOODS_LIST_SCOPE = 'MALL_GOODS';

/** nav_route 与 Vue 路由路径映射（开发 SQL 维护 nav_route 值） */
const ROUTE_MAP = {
  CakaHome: '/CakaHome',
  MoviesHome: '/MoviesHome',
  GiftPackageHome: '/GiftPackageHome',
  FoodBrandList: '/FoodBrandList',
  TaofukeBrandList: '/TaofukeBrandList',
};

/** 数据库是否配置了一级 nav_route（有则必走路由，不因 jump_page_flag 调 getEntrance） */
export function hasNavRoute(item) {
  return !!(item && item.navRoute);
}

/**
 * 一级是否为门户/聚合（仅首页宫格跳转；分类 Tab 左侧不展示此类一级）。
 */
export function isPortalL1(item) {
  if (!item) {
    return false;
  }
  if (hasNavRoute(item)) {
    return true;
  }
  const scene = item.categoryScene || '';
  return scene === SCENE.MALL_MOVIE || scene === SCENE.MALL_CAKE || scene === SCENE.GIFT_PORTAL;
}

export function normalizeNavItem(item) {
  if (!item) {
    return null;
  }
  return {
    id: item.categoryId != null ? item.categoryId : item.id,
    name: item.name,
    icon: item.icon,
    sortNum: item.sortNum,
    homeSortNum: item.homeSortNum,
    categoryScene: item.categoryScene,
    navRoute: item.navRoute,
    jumpPageFlag: item.jumpPageFlag,
    navLevel: item.navLevel,
    parentId: item.parentId,
    parentName: item.parentName,
    children: item.children,
  };
}

/**
 * 跳转聚合页前，将一级下的二级品牌列表写入 sessionStorage。
 *
 * @param {string} navRoute FoodBrandList | TaofukeBrandList
 * @param {Array} children 二级分类列表
 */
export function cacheAggregateChildren(navRoute, children) {
  const list = children || [];
  if (navRoute === 'FoodBrandList') {
    sessionStorage.setItem('foodBrandListData', JSON.stringify(list));
  } else if (navRoute === 'TaofukeBrandList') {
    sessionStorage.setItem('taofukeBrandListData', JSON.stringify(list));
  }
}

/** 换卡/登出时清理聚合品牌缓存 */
export function clearAggregateCategoryCache() {
  sessionStorage.removeItem('foodBrandListData');
  sessionStorage.removeItem('taofukeBrandListData');
}

/** 换卡时清理商品列表 + 聚合品牌缓存 */
export function clearCategoryNavCache() {
  sessionStorage.removeItem('goodsListCategoryData');
  clearAggregateCategoryCache();
}

/** 写入商品列表专用分类缓存（带 scope 标记，避免误用首页/分类页全量树） */
export function cacheGoodsListCategory(data) {
  sessionStorage.setItem('goodsListCategoryData', JSON.stringify({
    scope: GOODS_LIST_SCOPE,
    data: data || [],
  }));
}

/** 读取商品列表缓存，scope 不匹配则返回 null 强制重新拉取 */
export function readGoodsListCategoryCache() {
  const raw = sessionStorage.getItem('goodsListCategoryData');
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.scope === GOODS_LIST_SCOPE && Array.isArray(parsed.data)) {
      return parsed.data;
    }
    return null;
  } catch (e) {
    return null;
  }
}

/**
 * 在 MALL_GOODS 树中计算商品列表 Tab 定位下标。
 * tempList 结构为 [全部, ...children]，故二级下标 = 在 children 中的位置 + 1。
 */
export function findGoodsListIndices(item, tree) {
  const normalized = normalizeNavItem(item);
  if (!normalized || !tree || !tree.length) {
    return { index1: 0, index2: 0, indexName: normalized ? normalized.name : '' };
  }
  if (normalized.navLevel === 2 && normalized.parentId) {
    const index1 = tree.findIndex(c => c.id === normalized.parentId);
    const parent = index1 >= 0 ? tree[index1] : null;
    const children = (parent && parent.children) || [];
    const childIndex = children.findIndex(c => c.id === normalized.id);
    return {
      index1: index1 >= 0 ? index1 : 0,
      index2: childIndex >= 0 ? childIndex + 1 : 0,
      indexName: parent ? parent.name : (normalized.parentName || normalized.name),
    };
  }
  const index1 = tree.findIndex(c => c.id === normalized.id || c.name === normalized.name);
  return {
    index1: index1 >= 0 ? index1 : 0,
    index2: 0,
    indexName: normalized.name,
  };
}

/** 拉取 MALL_GOODS 分类树后跳转商品列表 */
export function navigateToGoodsList(vm, item) {
  const bathId = localStorage.getItem('bathId');
  const upgradeFlag = localStorage.getItem('upgradeFlag');
  const cityCode = localStorage.getItem('cityCode');
  return vm.$api.selectCategoryWithChildren(bathId, upgradeFlag, cityCode, GOODS_LIST_SCOPE)
    .then((res) => {
      if (res.code !== 0 || !res.data) {
        if (vm.$Toast) {
          vm.$Toast.fail(res.msg || '加载分类失败');
        }
        return;
      }
      cacheGoodsListCategory(res.data);
      const indices = findGoodsListIndices(item, res.data);
      vm.$router.push({
        path: '/GoodsList',
        query: {
          index1: indices.index1,
          index2: indices.index2,
          indexName: indices.indexName,
        },
      });
    });
}

export function prepareGiftMallSource() {
  localStorage.setItem('mallSource', 'giftPackage');
}

/**
 * 解析 Vue 路由 path。
 * nav_route 优先；无 nav_route 时用 scene 兜底（蛋糕/电影/礼包门户）。
 */
export function resolveCategoryRoute(item) {
  if (!item) {
    return null;
  }
  if (item.navRoute && ROUTE_MAP[item.navRoute]) {
    return ROUTE_MAP[item.navRoute];
  }
  const scene = item.categoryScene;
  if (scene === SCENE.MALL_CAKE) {
    return '/CakaHome';
  }
  if (scene === SCENE.MALL_MOVIE) {
    return '/MoviesHome';
  }
  if (scene === SCENE.GIFT_PORTAL) {
    return '/GiftPackageHome';
  }
  return null;
}

/**
 * 是否对该项调用 getEntrance。
 * 一级：有 nav_route 或门户 scene 时绝不调用；无 nav_route 时也不在一级点击调用（由右侧二级再调）。
 * 典型用于首页宫格上的二级/三方格子，或分类页右侧二级品牌。
 */
export function shouldOpenEntrance(item) {
  if (!item || !item.jumpPageFlag) {
    return false;
  }
  if (hasNavRoute(item)) {
    return false;
  }
  if (isPortalL1(item)) {
    return false;
  }
  // 首页宫格一级项：不直接 getEntrance（无 nav_route 时走商品列表等）；二级格子或右侧品牌才跳 H5
  if (item.navLevel === 1) {
    return false;
  }
  return true;
}

/** 首页宫格：顺序完全依赖后端 navItems（已按 home_sort_num 排好），仅做规范化与截取 */
export function buildNavGrid(navItems, max = 25) {
  return (navItems || []).map(normalizeNavItem).filter(Boolean).slice(0, max);
}

/**
 * 是否为品牌聚合列表页路由。
 *
 * @param {string} path Vue 路由 path
 * @returns {boolean}
 */
export function isAggregateRoute(path) {
  return path === '/FoodBrandList' || path === '/TaofukeBrandList';
}

/** 从活动/Banner 配置的一级分类中提取二级品牌列表 */
export function collectBrandListFromCategories(categoryList) {
  let brandList = [];
  (categoryList || []).forEach((category) => {
    if (category.children && category.children.length > 0) {
      brandList = brandList.concat(category.children);
    }
  });
  return brandList;
}

/** 根据一级分类 navRoute 解析品牌聚合页路由 */
export function resolveBrandListRoute(categoryList) {
  const first = (categoryList && categoryList[0]) || null;
  if (first && first.navRoute && ROUTE_MAP[first.navRoute]) {
    return {
      path: ROUTE_MAP[first.navRoute],
      navRoute: first.navRoute,
      title: first.name || '品牌列表',
    };
  }
  return {
    path: '/FoodBrandList',
    navRoute: 'FoodBrandList',
    title: (first && first.name) || '品牌列表',
  };
}

/**
 * 写入 queryTopicBooleanAndCategory 返回的活动/Banner 跳转字段。
 * showType=2 时同步缓存商品分类 Tab 数据。
 */
export function applyHotShowTopicResult(item, data) {
  if (!item || !data) {
    return;
  }
  item.flag = true;
  item.showType = data.showType;
  item.productId = data.productId;
  item.brandId = data.brandId;
  item.jumpPageFlag = data.jumpPageFlag || '';
  item.categoryList = data.categoryList;
  if (data.showType === '2' && data.categoryList && data.categoryList.length > 0) {
    sessionStorage.setItem('lableCategoryList', JSON.stringify(data.categoryList));
  }
}

/**
 * 活动图/Banner 点击后按 showType 跳转。
 * @param {object} vm Vue 实例
 * @param {object} item 含 showImgId、showType 等
 * @param {{ listHotType?: string, onBeforeH5Leave?: Function }} options
 */
export function navigateHotShowClick(vm, item, { listHotType = 'B', onBeforeH5Leave } = {}) {
  if (!item || !vm) {
    return;
  }
  const val = item;
  // 纯图通知由 Banner 组件 ImagePreview，不走商品跳转
  if (val.showType == '6') {
    return;
  }
  if (val.showType == '1' || val.showType == '2') {
    vm.$router.push({
      path: '/ActivityGoodsList',
      query: {
        activityId: val.showImgId.id,
        type: listHotType,
        showType: val.showType,
      },
    });
    return;
  }
  if (val.showType == '3') {
    if (val.productId) {
      vm.$router.push({
        path: '/Details',
        query: {
          id: val.productId,
          brandId: val.brandId,
        },
      });
    } else if (vm.$Toast) {
      vm.$Toast('敬请期待');
    }
    return;
  }
  if (val.showType == '4') {
    const categoryList = val.categoryList || [];
    const brandList = collectBrandListFromCategories(categoryList);
    if (brandList.length === 0) {
      clearAggregateCategoryCache();
      hotShowToast(vm, '敬请期待');
      return;
    }
    const { path, navRoute, title } = resolveBrandListRoute(categoryList);
    cacheAggregateChildren(navRoute, brandList);
    vm.$router.push({ path, query: { title } });
    return;
  }
  if (val.showType == '5') {
    if (val.jumpPageFlag) {
      if (typeof onBeforeH5Leave === 'function') {
        onBeforeH5Leave();
      }
      vm.$api.getEntrance(val.jumpPageFlag).then((res) => {
        if (res.code === 0 && res.data) {
          window.location.href = res.data;
        } else if (vm.$Toast) {
          vm.$Toast(res.msg || '跳转失败');
        }
      }).catch(() => {
        if (vm.$Toast) {
          vm.$Toast('跳转失败');
        }
      });
    } else if (vm.$Toast) {
      vm.$Toast('敬请期待');
    }
  }
}

function hotShowToast(vm, msg) {
  if (vm.$Toast) {
    vm.$Toast(msg);
  }
}

/**
 * 构建 queryTopicBooleanAndCategory 请求体。
 * formOverrides 可覆盖 cardId/cityCode 等（如商城 Home 使用 this.form）。
 */
export function buildHotShowTopicQuery(item, hotType, formOverrides = {}) {
  const requiredKeys = ['cardId', 'cityCode', 'upgradeFlag', 'bathId'];
  const missing = requiredKeys.filter((key) => {
    const val = formOverrides[key];
    return val == null || val === '';
  });
  if (missing.length) {
    return null;
  }
  return {
    cardId: formOverrides.cardId,
    cityCode: formOverrides.cityCode,
    upgradeFlag: formOverrides.upgradeFlag,
    bathId: formOverrides.bathId,
    size: 10,
    current: 1,
    hotType,
    id: item.showImgId.id,
  };
}

/**
 * 活动图/Banner/宫格入口：查询配置并按 showType 跳转（商城/蛋糕通用）。
 * @param {object} vm Vue 实例
 * @param {object} item 含 showImgId
 * @param {{ hotType?: string, listHotType?: string, formOverrides?: object }} options
 *   hotType — API 参数（A=Banner, B=活动图, D=蛋糕宫格等）
 *   formOverrides — 必填 cardId/cityCode/upgradeFlag/bathId
 *   onBeforeH5Leave — 跳转三方 H5 前回调
 */
export function queryAndNavigateHotShow(vm, item, { hotType = 'B', listHotType, formOverrides = {}, onBeforeH5Leave } = {}) {
  if (!vm || !item || !item.showImgId) {
    return Promise.resolve();
  }
  const activityListType = listHotType != null ? listHotType : hotType;
  const form = buildHotShowTopicQuery(item, hotType, formOverrides);
  if (!form) {
    hotShowToast(vm, '参数不完整');
    return Promise.resolve();
  }
  return vm.$api.queryTopicBooleanAndCategory(form)
    .then((res) => {
      if (!res.data) {
        hotShowToast(vm, res.msg || '查询失败');
        return;
      }
      if (res.data.flag) {
        applyHotShowTopicResult(item, res.data);
        navigateHotShowClick(vm, item, { listHotType: activityListType, onBeforeH5Leave });
      } else {
        hotShowToast(vm, '敬请期待');
      }
    })
    .catch(() => {});
}

import {
  PAGE_KEYS,
  SECTION_KEYS,
  consumePageViewState,
  getWindowScrollTop,
  isPageViewRestorePending,
  peekPageViewState,
  savePageViewState,
} from './pageScrollRestore';

/**
 * 跳转三方 H5 / 子页面前：保存首页纵向滚动 + 分类宫格 swipe 页码
 * @param {number} swipeIndex
 */
export function markHomeLeaveForH5(swipeIndex = 0) {
  savePageViewState(PAGE_KEYS.HOME, {
    scrollTop: getWindowScrollTop(),
    sections: { [SECTION_KEYS.NAV_SWIPE]: swipeIndex },
    fromH5: true,
  });
}

/** 是否存在待恢复的首页视图状态（含 H5 返回、详情/活动列表返回） */
export function isHomeH5ReturnPending() {
  return isPageViewRestorePending(PAGE_KEYS.HOME);
}

/** 读取待恢复的分类 swipe 页码（不清除标记） */
export function peekHomeNavSwipeIndex() {
  const state = peekPageViewState(PAGE_KEYS.HOME);
  if (!state || !state.sections) return 0;
  const index = parseInt(state.sections[SECTION_KEYS.NAV_SWIPE], 10);
  return Number.isNaN(index) ? 0 : index;
}

/** 清除首页视图恢复标记 */
export function clearHomeH5ReturnMark() {
  consumePageViewState(PAGE_KEYS.HOME);
}
